import React, { useState, useEffect } from 'react'
import { useCookieConsentStore } from '@/stores/cookieConsent'
import { clearUserAccessData } from '@/utils/userAccess'

const CookiePreferences: React.FC = () => {
  const { 
    showPreferences, 
    preferences, 
    updatePreferences, 
    savePreferences, 
    acceptAll, 
    hidePreferencesPanelAction,
    showCookiePolicyAction
  } = useCookieConsentStore()

  const [showBanner, setShowBanner] = useState(false)

  // Auto-hide banner after 3 seconds
  useEffect(() => {
    if (showBanner) {
      const timer = setTimeout(() => {
        setShowBanner(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [showBanner])

  if (!showPreferences) return null

  const handleToggle = (category: keyof typeof preferences) => {
    if (category === 'strictlyNecessary') return // Cannot disable strictly necessary cookies
    
    updatePreferences({
      [category]: !preferences[category]
    })
  }

  const handleStrictlyNecessaryClick = (event: React.MouseEvent) => {
    // Check if Ctrl key is pressed
    if (event.ctrlKey) {
      event.preventDefault()
      clearUserAccessData()
      
      // Show banner to inform user that cookie has been cleared
      setShowBanner(true)
    }
  }

  return (
    <React.Fragment>
      {/* Success Banner */}
      {showBanner && (
        <div className="fixed top-4 right-4 z-[60] bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-3 animate-in slide-in-from-right duration-300">
          <div className="flex-shrink-0">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">Cookie cleared successfully!</p>
            <p className="text-xs opacity-90">You may need to re-enter your information for access.</p>
          </div>
          <button
            onClick={() => setShowBanner(false)}
            className="flex-shrink-0 text-white hover:text-gray-200 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Cookie Preferences</h2>
            <button
              onClick={hidePreferencesPanelAction}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Intro Text */}
          <p className="text-sm text-gray-600 mb-4">
            You can choose which categories of cookies to allow. You may change your settings at any time. 
            Strictly necessary cookies are always active.
          </p>
          
          {/* Cookie Policy Link */}
          <div className="mb-6">
            <button
              onClick={showCookiePolicyAction}
              className="text-sm text-blue-600 underline hover:text-blue-800 transition-colors"
            >
              Read our Cookie Policy
            </button>
          </div>

          {/* Cookie Categories */}
          <div className="space-y-6">
            {/* Strictly Necessary Cookies */}
            <div 
              className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={handleStrictlyNecessaryClick}
              title="Ctrl+Click to clear eceptionist_user_access cookie and show confirmation"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-medium text-gray-900">1. Strictly Necessary Cookies</h3>
                <div className="flex items-center">
                  <span className="text-sm text-gray-500 mr-2">Always Active</span>
                  <div className="w-12 h-6 bg-gray-300 rounded-full flex items-center justify-end pr-1">
                    <div className="w-5 h-5 bg-gray-600 rounded-full"></div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                These cookies are required for the website to function and cannot be switched off. 
                They include security, form submissions, and load balancing.
              </p>
            </div>

            {/* Performance & Analytics Cookies */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-medium text-gray-900">2. Performance & Analytics Cookies</h3>
                <button
                  onClick={() => handleToggle('performanceAnalytics')}
                  className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${
                    preferences.performanceAnalytics ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                      preferences.performanceAnalytics ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <p className="text-sm text-gray-600">
                Help us understand how visitors use our website (e.g., Google Analytics). 
                They provide anonymous data so we can improve site performance.
              </p>
            </div>

            {/* Functional Cookies */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-medium text-gray-900">3. Functional Cookies</h3>
                <button
                  onClick={() => handleToggle('functional')}
                  className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${
                    preferences.functional ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                      preferences.functional ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <p className="text-sm text-gray-600">
                Allow the site to remember your preferences (e.g., language, region) and enhance features.
              </p>
            </div>

            {/* Advertising / Targeting Cookies */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-medium text-gray-900">4. Advertising / Targeting Cookies</h3>
                <button
                  onClick={() => handleToggle('advertisingTargeting')}
                  className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${
                    preferences.advertisingTargeting ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                      preferences.advertisingTargeting ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <p className="text-sm text-gray-600">
                Used to deliver more relevant ads and measure the effectiveness of campaigns. 
                These may be set by us or third-party partners.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <button
              onClick={savePreferences}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-md hover:bg-blue-700 transition-colors"
            >
              Save Settings
            </button>
            <button
              onClick={acceptAll}
              className="flex-1 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
            >
              Accept All
            </button>
          </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

export default CookiePreferences
